import * as signalR from "@microsoft/signalr";
import { app } from "../Frame/App";
import * as toastr from "../../lib/toastr/toastr.min";
import { EventDispatcher } from "../Events/EventDispatcher";
import { DynPopup } from "../Components/DynPopup";
import dom from "../../dist/jsx-render/dom";

export class LockManager {

    private saveButton: HTMLElement;
    private cancelButton: HTMLElement;
    private deleteButton: HTMLElement;
    private entityType: string;
    private entityId: number = null;
    private entityDisplayName: string;
    editPageConnection: signalR.HubConnection;
    listPageConnection: signalR.HubConnection;

    private requestEditContainer: HTMLElement;

    public get onRequestEditCreated() {
        return this.onRequestEdit.asEvent();
    }
    private onRequestEdit = new EventDispatcher<LockManager, number>();

    public get onRequestEditDeniedCreated() {
        return this.onRequestEditDenied.asEvent();
    }
    private onRequestEditDenied = new EventDispatcher<LockManager, number>();

    public get onRequestEditApprovedCreated() {
        return this.onRequestEditApproved.asEvent();
    }
    private onRequestEditApproved = new EventDispatcher<LockManager, number>();

    public get onRequestEditTimeoutCreated() {
        return this.onRequestEditTimeout.asEvent();
    }
    private onRequestEditTimeout = new EventDispatcher<LockManager, number>();

    public get onReloadObjectCreated() {
        return this.onReloadObject.asEvent();
    }
    private onReloadObject = new EventDispatcher<LockManager, number>();

    private lockMessages: HTMLElement[];

    private dynamicEditRequestElement: HTMLElement = null;
    private dynamicEditRequestElementOpen: boolean = false;

    // Entry Points
    getLockableInfo = function (entityType: string): boolean {
        if (entityType) {

            var request;
            if (window.XMLHttpRequest) {
                request = new XMLHttpRequest();
            }

            var tempRequest = request;
            var isLockable;

            request.onreadystatechange = function () {
                if (tempRequest.readyState == 4 && tempRequest.status == 200) {

                    let objLockableInfo = JSON.parse(tempRequest.responseText);

                    isLockable = objLockableInfo.isLockable;
                }
            };

            request.open("GET", app.resolveUrl("~/api/common/getLockableInfo/" + entityType), false);
            request.send();

            return isLockable;
        }
    }

    requestLock = async (entityId: number, entityType: string, contextWindow: Window = null) => {
        if (entityId && entityId != 0) {
            if (!contextWindow) contextWindow = window;

            this.editPageConnection = this.getSignalRConnection();
            this.initializeSingleEntityLocking(entityId, entityType, contextWindow);
            this.editPageConnection.start().then(() => {
                this.editPageConnection.invoke("AcquireLock", entityId, entityType);
            });
        }
    }

    requestEdit = async (entityId: number, entityType: string) => {
        if (entityId && entityId != 0) {

            if (this.editPageConnection?.state == signalR.HubConnectionState.Connected) {
                this.editPageConnection.invoke("RequestEdit", entityId, entityType);
            } 
        }

    }

    editDenied = async (entityId: number, entityType: string) => {
        if (entityId && entityId != 0) {

            if (this.editPageConnection?.state == signalR.HubConnectionState.Connected) {
                this.editPageConnection.invoke("EditDenied", entityId, entityType);
            }
        }

    }

    editApproved = async (entityId: number, entityType: string) => {
        if (entityId && entityId != 0) {

            if (this.editPageConnection?.state == signalR.HubConnectionState.Connected) {
                this.editPageConnection.invoke("EditApproved", entityId, entityType);
            }
        }

    }

    watchEntityList = (entityType: string, contextWindow: Window) => {
        this.listPageConnection = this.getSignalRConnection();

        this.setProperties(entityType);
        this.initializeListUpdating();
        this.initDisconnectListWatch(contextWindow);

        this.listPageConnection.start().then(() => {
            this.getLockedEntities(entityType);
            this.InitWatchList(entityType);
        });
    }


    // Initializers
    private initializeSingleEntityLocking = (entityId: number, entityType: string, contextWindow: Window) => {
        this.editPageConnection = this.getSignalRConnection();
        let contextDocument = contextWindow.document as Document;

        this.initToastrOptions();

        this.setProperties(entityType, entityId);
        this.getEntityDisplayName(entityType);
        this.getButtons(contextDocument);
        this.releaseLockOnClickButton(contextWindow);
        this.disableEditForm(contextDocument, entityType);
        this.informWaitingGroup(contextDocument);
        this.initReleaseLockOnUnloadWindow(contextWindow);
        this.loadConcurrencyNotifier();
        this.loadRequestEditNotifier();
        this.loadRequestEditCancel();
        this.loadRequestEditDeniedNotifier();
        this.loadRequestEditApprovedNotifier();
        this.NotifyOtherUserWhenMeasureReleased();
        
    }

    private initializeListUpdating = () => {

        this.initUpdateListLockAcquired();
        this.initUpdateListLockReleased();
    }

    private initToastrOptions = () => {
        toastr.options = {
            "closeButton": true,
            "positionClass": "toast-top-center",
            "timeOut": "0",
            "extendedTimeOut": "0",
            "preventDuplicates": true,
        }
    }

    // Common
    private setProperties = (entityType: string, entityId?: number) => {
        this.entityType = entityType;
        if (entityId) this.entityId = entityId;
    }

    private getSignalRConnection = () => {
        return new signalR.HubConnectionBuilder()
            .withUrl(app.resolveUrl("~/lockInformationHub"))
            .build();
    }


    // ListPage Functions 
    private InitWatchList = (entityType: string) => {
        this.listPageConnection.invoke("WatchList", entityType);
    }

    private initUpdateListLockAcquired = () => {
        this.listPageConnection.on("updateListLockAcquired", function (result) {
            let lockIcon = document.querySelector(`.mt-content-row[data-id='${result.id}'] .lock div`) as HTMLElement;
            lockIcon.classList.add("fa-lock");
        });
    }

    private initUpdateListLockReleased = () => {
        this.listPageConnection.on("updateListLockReleased", function (result) {
            let lockIcon = document.querySelector(`.mt-content-row[data-id='${result.id}'] .lock div`) as HTMLElement;
            lockIcon.classList.remove("fa-lock");
        });
    }

    private getLockedEntities = (entityType: string) => {
        this.listPageConnection.on("getLockedEntities", function (result) {
            result.forEach((id) => {
                let lockIcon = document.querySelector(`.mt-content-row[data-id='${id}'] .lock div`) as HTMLElement;
                lockIcon.classList.add("fa-lock");
            });
        });

        this.listPageConnection.invoke("GetLockedEntities", entityType);
    }

    // EditPage Functions  
    private getEntityDisplayName = (entityType: string) => {
        this.entityDisplayName = Resources.Common[entityType];
    }

    private getButtons = (contextDocument: Document) => {
        this.saveButton = contextDocument.getElementById('msoModalButtonSaveClose');
        this.cancelButton = contextDocument.getElementById('msoModalButtonCancel');
        this.deleteButton = contextDocument.querySelector('button[data-id="menuItemDelete"]');
    }

    private informWaitingGroup = (contextDocument: Document) => {
        this.editPageConnection.on("informWaitingGroup", () => {
            let text: string = Resources.Common.infoLockReleased;
            text = text.replace(/PLACEHOLDER/g, this.entityDisplayName);

            toastr.success(text);
        });
    }

    /**
     * Set events for Modalbuttons used in Administration
     * @param contextWindow
     * @param eventButtons
     */
    private releaseLockOnClickButton = (contextWindow: Window) => {
        if (this.saveButton) {
            this.saveButton.addEventListener('click', this.releaseLock);
        }
        if (this.cancelButton) {
            contextWindow["app"].cancelEvents.push(this.destroyAllMessages);
            contextWindow["app"].cancelEvents.push(this.releaseLock);
        }
    }

    private destroyAllMessages = () => {
        toastr.clear();
    }

    public releaseLock = () => {
        this.releaseLockWithCallback();
    }

    public releaseLockWithCallback = () => {
        console.log("Releasing lock...");

        if (this.editPageConnection?.state == signalR.HubConnectionState.Connected) {

            return this.editPageConnection.invoke("ReleaseLock", this.entityId, this.entityType)
                .then(
                    () => {
                        console.log("stopping connection after lock was released...");
                        return this.editPageConnection.stop();
                    },
                    (reason) => {
                        console.log(`Error calling releaseLock.\n${reason}`);
                    });
        } else {
            // Since the connection is already closed for whatever reason, we still need to return a promise,
            // so the .then callback defined by the caller still takes place.
            return Promise.resolve();
        }
    }
    
    // Kann wahrscheinlich weg.
    private stopConnection = () => {
        if (this.editPageConnection?.state == signalR.HubConnectionState.Connected) {
            console.log("stopping connection...");
            this.editPageConnection.stop()
                .then(() => { console.log("connection stopped.") });
        }
    }

    private disableEditForm = (contextDocument: Document, entityType: string) => {
        this.editPageConnection.on("disableEditForm", (userName: string) => {
            let inputList = contextDocument.querySelectorAll("input");
            let selectList = contextDocument.querySelectorAll("select");
            let tableList = contextDocument.querySelectorAll("table");
            let richtextEditor = contextDocument.querySelectorAll(".note-editable");
            let textareaList = contextDocument.querySelectorAll("textarea");
            let docUpload = contextDocument.querySelectorAll(".doc-upload");
            let docLinks = contextDocument.querySelectorAll(".doc-link");
            let dynamicActions = contextDocument.querySelectorAll(".dyn-action");

            inputList.forEach((input) => {
                input.disabled = true;
            })
            selectList.forEach((select) => {
                select.disabled = true;
            })
            tableList.forEach((table) => {
                table.classList.add("bg-light", "text-muted");
            })

            richtextEditor.forEach((editor) => {
                editor.setAttribute("contenteditable", "false");
            })

            textareaList.forEach((textareaEditor) => {
                textareaEditor.setAttribute("disabled", "true");
            })

            docUpload.forEach((link) => {
                link.classList.add("d-none");
            });
            docLinks.forEach((link) => {
                link.classList.add("d-none");
            });
            dynamicActions.forEach((link) => {
                link.classList.add("d-none");
            });

            if (this.saveButton)
                this.saveButton.setAttribute("disabled", undefined);

            if (this.deleteButton)
                this.deleteButton.setAttribute("disabled", undefined);

            // Create red box
            let text: string;

            if (userName) {
                text = Resources.Common.infoLockedEntityWithUser;
                text = text.replace(/PLACEHOLDER1/g, userName);
                text = text.replace(/PLACEHOLDER2/g, this.entityDisplayName);
            } else {
                text = Resources.Common.infoLockedEntity;
                text = text.replace(/PLACEHOLDER/g, this.entityDisplayName);
            }

            let lockedMessage = <div>{text}</div>;

            if (entityType == "measure") {
                this.requestEditContainer = <div class="lock-request"></div>;
                lockedMessage.appendChild(this.requestEditContainer);
                this.requestEditContainer.appendChild(this.getRequestEditButton());
            }

            toastr.options["closeButton"] = false;
            toastr.options["tapToDismiss"] = false;
            toastr.options["positionClass"] = "toast-top-full-width";
            toastr["error"](lockedMessage);
           
        });
    }

    private getRequestEditButton(): HTMLElement {
        let requestEditButton: HTMLElement = <button type="button" class="btn btn-mso pag-button mr-2">{Resources.Common.btnRequestEdit}</button>;
        requestEditButton.addEventListener("click", this.onRequestEditClicked);
        return requestEditButton;
    }

    private getWaitForEditApproval(): HTMLElement {
        return <label className="form-label">{Resources.Common.infoWaitForRequestApproval}</label>;
    }

    private onRequestEditClicked = () => {
        if (this.onRequestEdit) {
            app.destroyDomChildren(this.requestEditContainer);
            this.requestEditContainer.appendChild(this.getWaitForEditApproval());
            this.onRequestEdit.dispatch(this, 0);
        }

        return false;
    }

    private onRequestEditDeniedClicked = () => {
        if (this.onRequestEditDenied) {
            this.onRequestEditDenied.dispatch(this, 0);
        }
    }

    private onRequestEditApprovedClicked = () => {
        if (this.onRequestEditApproved) {
            this.onRequestEditApproved.dispatch(this, 0);
        }
    }

    private onRequestEditTimedout = () => {
        if (this.onRequestEditTimeout) {
            this.onRequestEditTimeout.dispatch(this, 0);
        }
    }

    private onReloadObjectCommand = () => {
        if (this.onReloadObject) {
            this.onReloadObject.dispatch(this, 0);
        }
    }

    private initReleaseLockOnUnloadWindow = (contextWindow: Window) => {
        contextWindow.addEventListener("beforeunload", (event: Event) => {
            this.releaseLock();
        });
    }

    private initDisconnectListWatch = (contextWindow: Window) => {
        contextWindow.addEventListener("beforeUnload", () => {
            this.listPageConnection.stop();
        });
    }

    private loadConcurrencyNotifier = () => {
        this.editPageConnection.on("loadConcurrencyNotifier", () => {
            this.editPageConnection.on("notifyConcurrency", () => {

                let text: string = Resources.Common.infoConcurrentUser;
                text = text.replace(/PLACEHOLDER/g, this.entityDisplayName);

                toastr.info(text);
            })
        })
    }

    private loadRequestEditNotifier = () => {

        this.editPageConnection.on("notifyRequestEdit", (timeout: number) => {
            this.dynamicEditRequestElementOpen = true;

            this.dynamicEditRequestElement = <DynPopup size="fit" showFooter={false}
                customButtons={[
                    {
                        text: Resources.Common.buttonStayInMeasure,
                        action: () => {
                            this.dynamicEditRequestElementOpen = false;
                            app.destroyDomElements(this.dynamicEditRequestElement);
                            this.onRequestEditDeniedClicked();
                        },
                        cssClasses: "btn btn-cancel btn-mso"
                    },
                    {
                        text: Resources.Common.buttonSaveAndCloseMeasure,
                        action: () => {
                            this.dynamicEditRequestElementOpen = false;
                            app.destroyDomElements(this.dynamicEditRequestElement);
                            this.onRequestEditApprovedClicked();
                        },
                        cssClasses: "btn btn-save btn-mso"
                    }
                ]}
                title={Resources.Common.titleRequestEditRights}>
                <label className="form-label">{Resources.Common.infoRequestEditRights}</label>
            </DynPopup>;

            document.body.appendChild(this.dynamicEditRequestElement);

            setTimeout(() => {
                if (this.dynamicEditRequestElementOpen) {
                    this.dynamicEditRequestElementOpen = false;
                    app.destroyDomElements(this.dynamicEditRequestElement);
                    this.onRequestEditTimedout();
                }
            }, timeout);
        })
    }

    private loadRequestEditCancel = () => {
        this.editPageConnection.on("notifyRequestCancel", () => {
            if (this.dynamicEditRequestElementOpen) {
                if (this.dynamicEditRequestElement != null) {
                    app.destroyDomElements(this.dynamicEditRequestElement);
                    this.dynamicEditRequestElement = null;
                }

                this.dynamicEditRequestElementOpen = false;
            }
        });
    }

    private loadRequestEditDeniedNotifier = () => {
        this.editPageConnection.on("notifyRequestDenied", () => {
            app.destroyDomChildren(this.requestEditContainer);
            this.requestEditContainer.appendChild(this.getRequestEditButton());

            toastr.warning(Resources.Common.infoRequestForEditDenied);
        })
    }

    private loadRequestEditApprovedNotifier = () => {
        this.editPageConnection.on("notifyRequestApproved", () => {
            this.onReloadObjectCommand();
        })
    }

    private NotifyOtherUserWhenMeasureReleased = () => {
        this.editPageConnection.on("notifyMaßnahmeFreigeben", () => {

            let text: string = Resources.Common.infoReleaseMeasure;
            text = text.replace(/PLACEHOLDER/g, this.entityDisplayName);

            toastr.warning(text);


            let toastInfoElement = document.querySelector(".toast.toast-info") as HTMLElement;
            if (toastInfoElement) {
                let closeButton = toastInfoElement.querySelector(".toast-close-button") as HTMLButtonElement;
                closeButton?.click();
            }
        })
    }
}